import React from "react";

import {
  actions,
  functionalUpdate,
  ensurePluginOrder,
  useGetLatest,
  useMountedLayoutEffect,
} from "react-table";

import { encodeAfterCursor } from "../helpers/cursorCodec";

// Merged pagination plugin for <DataTable>. Replaces the near-identical
// usePageCursor / usePageCursorRedux plugins. Supports two modes off the
// standard react-table `manualPagination` flag:
//   - server  (manualPagination: true)  — paginate a HotChocolate [UsePaging]
//       cursor connection. Navigation (prev/next/jump) derives the `after`
//       cursor directly from the target page position via cursorCodec.
//   - client  (manualPagination: false) — slice a fully-loaded row array in
//       the browser (the ex-SortTable behaviour).
// "Page X of Y" and jump-to-page work in both modes because totalCount is
// enabled globally on the server (Host.cs) and rows.length is known on the
// client.

// react-table expand parity: only used when a consumer passes
// `paginateExpandedRows={false}` (DataTable leaves the default `true`, so this
// path is currently inert) — kept so expandable consumers behave correctly.
function expandRows(
  rows,
  { manualExpandedKey, expanded, expandSubRows = true }
) {
  const expandedRows = [];

  const handleRow = (row) => {
    row.isExpanded =
      (row.original && row.original[manualExpandedKey]) || expanded[row.id];

    row.canExpand = row.subRows && !!row.subRows.length;

    expandedRows.push(row);

    if (expandSubRows && row.subRows && row.subRows.length && row.isExpanded) {
      row.subRows.forEach(handleRow);
    }
  };

  rows.forEach(handleRow);

  return expandedRows;
}

const pluginName = "useCursorPagination";

// Actions
actions.resetPage = "resetPage";
actions.gotoPage = "gotoPage";
actions.setPageSize = "setPageSize";

export const useCursorPagination = (hooks) => {
  hooks.stateReducers.push(reducer);
  hooks.useInstance.push(useInstance);
};

useCursorPagination.pluginName = pluginName;

function reducer(state, action, previousState, instance) {
  if (action.type === actions.init) {
    return {
      pageIndex: 0,
      cursor: null,
      previousCursors: [],
      ...state,
    };
  }

  // User-initiated sort/filter changes redefine the result set's cursor space,
  // so snap to page 1 in the SAME state update. Doing it atomically (rather
  // than via an effect) means no intermediate render with new-sort + stale-
  // cursor exists for the data-fetch effect to fire on — so a sort change is a
  // single, correct page-1 fetch. We intercept ONLY the user-initiated sort/
  // filter actions (a header click dispatches `toggleSortBy`), never the
  // autoReset "reset*" actions (those fire on every server data change and
  // would loop). This reducer runs after useSortBy/useFilters in the chain, so
  // `state` already carries the new sort/filter — we just reset paging.
  if (
    action.type === "toggleSortBy" ||
    action.type === "setSortBy" ||
    action.type === "clearSortBy" ||
    action.type === "setFilter" ||
    action.type === "setAllFilters" ||
    action.type === "setGlobalFilter"
  ) {
    return { ...state, pageIndex: 0, cursor: null, previousCursors: [] };
  }

  if (action.type === actions.resetPage) {
    // Reset to the first page AND clear the cursor. The old plugin left a
    // stale `cursor` here, so a filter/sort/page-size change refetched the
    // wrong slice — fixed in the merge.
    return {
      ...state,
      pageIndex: instance.initialState.pageIndex || 0,
      cursor: null,
      previousCursors: [],
    };
  }

  if (action.type === actions.gotoPage) {
    const { pageCount, manualPagination, keysetCursor } = instance;
    const newPageIndex = functionalUpdate(action.pageIndex, state.pageIndex);

    if (newPageIndex < 0 || (pageCount !== -1 && newPageIndex > pageCount - 1)) {
      return state;
    }

    if (!manualPagination) {
      // Client mode: the slice happens locally, no cursor needed.
      return { ...state, pageIndex: newPageIndex };
    }

    if (keysetCursor) {
      // Keyset/opaque cursor (e.g. the bespoke Cursor.cs — LastId of the last
      // row seen). The backend returns the page's `endCursor`; we thread it for
      // forward nav and keep a previousCursors stack for back nav. Jump-to-page
      // is unsupported here (DataTable hides the input in keyset mode), so only
      // ±1 prev/next reaches this branch.
      const previousCursors = state.previousCursors ?? [];
      if (newPageIndex > state.pageIndex) {
        return {
          ...state,
          pageIndex: newPageIndex,
          cursor: instance.endCursor ?? null,
          previousCursors: [...previousCursors, state.cursor ?? null],
        };
      }
      return {
        ...state,
        pageIndex: newPageIndex,
        cursor: previousCursors.length
          ? previousCursors[previousCursors.length - 1]
          : null,
        previousCursors: previousCursors.slice(0, -1),
      };
    }

    // Server mode: derive the `after` cursor straight from the target page
    // position (the default HotChocolate cursor is a positional index).
    return {
      ...state,
      pageIndex: newPageIndex,
      cursor: encodeAfterCursor(newPageIndex, state.pageSize),
    };
  }

  if (action.type === actions.setPageSize) {
    // Changing page size redefines the cursor space, so reset to page 1
    // (FR-4: per-page change resets to the first page).
    return {
      ...state,
      pageIndex: 0,
      cursor: null,
      previousCursors: [],
      pageSize: action.pageSize,
    };
  }
}

function useInstance(instance) {
  const {
    rows,
    autoResetPage = true,
    manualExpandedKey = "expanded",
    plugins,
    totalCount: userTotalCount,
    paginateExpandedRows = true,
    expandSubRows = true,
    state: { pageSize, pageIndex, expanded },
    dispatch,
    data,
    manualPagination,
  } = instance;

  ensurePluginOrder(
    plugins,
    ["useGlobalFilter", "useFilters", "useGroupBy", "useSortBy", "useExpanded"],
    "useCursorPagination"
  );

  const getAutoResetPage = useGetLatest(autoResetPage);

  useMountedLayoutEffect(() => {
    if (getAutoResetPage()) {
      dispatch({ type: actions.resetPage });
    }
  }, [
    dispatch,
    // Reset to page 1 when the (client-mode) source data prop changes. In
    // server mode `data` is the freshly-fetched page, so it's nulled (resetting
    // on it would loop). Sort/filter resets are handled atomically in the
    // reducer above — intentionally not listed here.
    manualPagination ? null : data,
  ]);

  const pageCount = manualPagination
    ? typeof userTotalCount === "number"
      ? Math.ceil(userTotalCount / pageSize)
      : -1
    : Math.ceil(rows.length / pageSize);

  const page = React.useMemo(() => {
    let page;

    if (manualPagination) {
      page = rows;
    } else {
      const pageStart = pageSize * pageIndex;
      const pageEnd = pageStart + pageSize;
      page = rows.slice(pageStart, pageEnd);
    }

    if (paginateExpandedRows) {
      return page;
    }

    return expandRows(page, { manualExpandedKey, expanded, expandSubRows });
  }, [
    expandSubRows,
    expanded,
    manualExpandedKey,
    manualPagination,
    pageIndex,
    pageSize,
    paginateExpandedRows,
    rows,
  ]);

  // Position is authoritative: with jump-to-page the connection's pageInfo can
  // lag a request, so derive bounds from pageCount (backed by totalCount) when
  // it's known, falling back to pageInfo only when the total is unknown.
  const canPreviousPage = pageIndex > 0;
  const canNextPage =
    pageCount === -1
      ? (instance.pageInfo?.hasNextPage ?? false)
      : pageIndex < pageCount - 1;

  // Single entry point for navigation. Accepts an absolute 0-based page index
  // (jump-to-page) or a functional updater (prev/next). The reducer turns it
  // into the right cursor for server mode or a local slice for client mode.
  const gotoPage = React.useCallback(
    (pageIndex) => {
      dispatch({ type: actions.gotoPage, pageIndex });
    },
    [dispatch]
  );

  const previousPage = React.useCallback(
    () => gotoPage((old) => old - 1),
    [gotoPage]
  );

  const nextPage = React.useCallback(
    () => gotoPage((old) => old + 1),
    [gotoPage]
  );

  const setPageSize = React.useCallback(
    (pageSize) => {
      dispatch({ type: actions.setPageSize, pageSize });
    },
    [dispatch]
  );

  Object.assign(instance, {
    pageCount,
    page,
    canPreviousPage,
    canNextPage,
    gotoPage,
    previousPage,
    nextPage,
    setPageSize,
  });
}
